import config from '../utils/config';

class ApiService {
  constructor() {
    this.baseURL = config.apiBaseUrl;
    this.uploadBaseURL = config.uploadBaseUrl || config.apiBaseUrl;
    this.token = localStorage.getItem(config.tokenKey);
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem(config.tokenKey, token);
    } else {
      localStorage.removeItem(config.tokenKey);
    }
  }

  getToken() {
    return this.token || localStorage.getItem(config.tokenKey);
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async request(endpoint, options = {}) {
    // Normalize endpoint - remove leading slash if present
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${this.baseURL}/${normalizedEndpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
      credentials: 'include',
    };

    try {
      const response = await fetch(url, config);
      
      // Get response text first
      const text = await response.text();
      let data;
      
      // Try to parse as JSON regardless of content-type header
      // Some endpoints might not set the header correctly
      if (text.trim()) {
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          // Check if it's actually a JSON content-type
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            console.error('JSON Parse Error:', parseError);
            console.error('Response text:', text);
            throw new Error('Invalid JSON response from server');
          } else {
            // Not JSON content-type, return the text as error
            throw new Error(text || 'Invalid response format');
          }
        }
      } else {
        // Empty response - check status code
        if (response.ok) {
          data = {};
        } else {
          throw new Error('Empty response from server');
        }
      }

      if (!response.ok) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      console.error('URL:', url);
      throw error;
    }
  }

  // ==================== AUTH ENDPOINTS ====================
  
  async login(email, password) {
    const response = await this.request('auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.data?.token) {
      this.setToken(response.data.token);
    }
    
    return response;
  }

  async register(email, password) {
    return this.request('auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getCurrentUser() {
    return this.request('auth/me');
  }

  async forgotPassword(email) {
    return this.request('auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, password) {
    return this.request('auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async validateResetToken(token) {
    return this.request(`auth/reset-password?token=${token}`, {
      method: 'GET',
    });
  }

  async approveUser(userId, roleId, clientId = null) {
    return this.request('auth/approve', {
      method: 'POST',
      body: JSON.stringify({ 
        user_id: userId, 
        role_id: roleId,
        client_id: clientId 
      }),
    });
  }

  async getPendingUsers() {
    return this.request('auth/pending');
  }

  async getUsers() {
    return this.request('auth/users');
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_permissions');
    localStorage.removeItem('user_id');
  }

  // ==================== ENQUIRY ENDPOINTS ====================
  
  async getEnquiries(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`enquiries${queryString ? `?${queryString}` : ''}`);
  }

  async getEnquiry(id) {
    return this.request(`enquiries/${id}`);
  }

  async createEnquiry(data) {
    return this.request('enquiries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async searchEnquiryById(id) {
    return this.request(`enquiries/search/${id}`);
  }

  async searchEnquiriesByCompany(companyName) {
    return this.request(`enquiries/company/${encodeURIComponent(companyName)}`);
  }

  async searchEnquiriesByDomain(domain) {
    return this.request(`enquiries/domain/${encodeURIComponent(domain)}`);
  }

  // ==================== CLIENT ENDPOINTS ====================
  
  async getClients(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`clients${queryString ? `?${queryString}` : ''}`);
  }

  async getClient(clientId) {
    return this.request(`clients/${clientId}`);
  }

  async createClient(data) {
    return this.request('clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(clientId, data) {
    return this.request(`clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteClient(clientId) {
    return this.request(`clients/${clientId}`, {
      method: 'DELETE',
    });
  }

  async getClientTransactions(clientId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`clients/${clientId}/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getClientPaymentSummary(clientId) {
    return this.request(`clients/${clientId}/payment-summary`);
  }

  // ==================== TRANSACTION ENDPOINTS ====================
  
  async getTransactions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getTransaction(id) {
    return this.request(`transactions/${id}`);
  }

  async createTransaction(data) {
    return this.request('transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTransaction(id, data) {
    return this.request(`transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTransaction(id) {
    return this.request(`transactions/${id}`, {
      method: 'DELETE',
    });
  }

  async getTransactionStatistics() {
    return this.request('transactions/statistics');
  }

  async getPaymentMethods() {
    return this.request('transactions/payment-methods');
  }

  // ==================== ANALYTICS ENDPOINTS ====================
  
  async getAnalytics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`analytics${queryString ? `?${queryString}` : ''}`);
  }

  // ==================== ROLE ENDPOINTS ====================
  
  async getRoles() {
    return this.request('roles');
  }

  async getRole(roleId) {
    return this.request(`roles/${roleId}`);
  }

  async createRole(data) {
    return this.request('roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(roleId, data) {
    return this.request(`roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(roleId) {
    return this.request(`roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async getRoleHierarchy() {
    return this.request('roles/hierarchy');
  }

  async getRolePermissions(roleId) {
    return this.request(`roles/${roleId}/permissions`);
  }

  // ==================== PERMISSION ENDPOINTS ====================
  
  async getPermissions() {
    return this.request('permissions');
  }

  async getPermission(permissionId) {
    return this.request(`permissions/${permissionId}`);
  }

  async createPermission(data) {
    return this.request('permissions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePermission(permissionId, data) {
    return this.request(`permissions/${permissionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePermission(permissionId) {
    return this.request(`permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }

  async getPermissionResources() {
    return this.request('permissions/resources');
  }

  // ==================== USER HIERARCHY ENDPOINTS ====================
  
  async assignUserChild(parentUserId, childUserId) {
    return this.request('users/hierarchy', {
      method: 'POST',
      body: JSON.stringify({
        parent_user_id: parentUserId,
        child_user_id: childUserId,
      }),
    });
  }

  async removeUserChild(parentUserId, childUserId) {
    return this.request('users/hierarchy', {
      method: 'DELETE',
      body: JSON.stringify({
        parent_user_id: parentUserId,
        child_user_id: childUserId,
      }),
    });
  }

  async getUserChildren(userId) {
    return this.request(`users/${userId}/children`);
  }

  async getUserParent(userId) {
    return this.request(`users/${userId}/parent`);
  }

  async getAllUserHierarchies() {
    return this.request('users/hierarchy/all');
  }

  // ==================== FORM CONFIG ENDPOINTS ====================
  
  async getFormConfigs(activeOnly = true) {
    return this.request(`form-configs?active_only=${activeOnly}`);
  }

  async getFormConfigById(id) {
    return this.request(`form-configs/${id}`);
  }

  async getFormConfigByKey(configKey) {
    return this.request(`form-configs/key/${encodeURIComponent(configKey)}`);
  }

  async createFormConfig(data) {
    return this.request('form-configs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFormConfig(id, data) {
    return this.request(`form-configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFormConfig(id) {
    return this.request(`form-configs/${id}`, {
      method: 'DELETE',
    });
  }

  async duplicateFormConfig(id) {
    return this.request(`form-configs/${id}/duplicate`, {
      method: 'POST',
    });
  }

  // ==================== SECURITY ENDPOINTS ====================
  
  async getCsrfToken() {
    return this.request('security/csrf');
  }

  async getJsToken() {
    return this.request('security/token');
  }

  async getCaptcha() {
    return this.request('security/captcha');
  }

  // ==================== ADMIN / KEY ROTATION ENDPOINTS ====================
  
  async rotateEncryptionKeys(adminToken, data = {}) {
    return this.request('admin/rotate-keys', {
      method: 'POST',
      headers: {
        'X-Admin-Token': adminToken,
      },
      body: JSON.stringify(data),
    });
  }

  async getRotationStatus(adminToken) {
    return this.request('admin/rotation-status', {
      method: 'GET',
      headers: {
        'X-Admin-Token': adminToken,
      },
    });
  }

  // ==================== FILE UPLOAD ENDPOINTS ====================
  
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.uploadBaseURL}/upload`;
    const token = this.getToken();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      return data;
    } catch (error) {
      console.error('Upload Error:', error);
      throw error;
    }
  }

  // =====================
  // Services
  // =====================
  
  /**
   * Get all services (optionally with sub-services)
   */
  async getServices(withSubServices = false, activeOnly = true) {
    const params = new URLSearchParams();
    if (withSubServices) params.append('with_sub_services', 'true');
    if (activeOnly) params.append('active_only', 'true');
    
    return this.request(`services?${params.toString()}`);
  }

  /**
   * Get service by ID
   */
  async getService(id) {
    return this.request(`services/${id}`);
  }

  /**
   * Create new service (Admin only)
   */
  async createService(serviceData) {
    return this.request('services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  /**
   * Update service (Admin only)
   */
  async updateService(id, serviceData) {
    return this.request(`services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  /**
   * Delete service (Admin only)
   */
  async deleteService(id) {
    return this.request(`services/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get sub-services by service ID
   */
  async getSubServices(serviceId, activeOnly = true) {
    const params = new URLSearchParams();
    if (activeOnly) params.append('active_only', 'true');
    
    return this.request(`services/${serviceId}/sub-services?${params.toString()}`);
  }

  /**
   * Create sub-service (Admin only)
   */
  async createSubService(subServiceData) {
    return this.request('services/sub-services', {
      method: 'POST',
      body: JSON.stringify(subServiceData),
    });
  }

  /**
   * Update sub-service (Admin only)
   */
  async updateSubService(id, subServiceData) {
    return this.request(`services/sub-services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(subServiceData),
    });
  }

  /**
   * Delete sub-service (Admin only)
   */
  async deleteSubService(id) {
    return this.request(`services/sub-services/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get services for a specific client
   */
  async getClientServices(clientId) {
    return this.request(`clients/${clientId}/services`);
  }

  /**
   * Assign services to client (Admin only)
   */
  async assignServicesToClient(clientId, servicesData) {
    return this.request(`clients/${clientId}/services`, {
      method: 'POST',
      body: JSON.stringify(servicesData),
    });
  }
}

const api = new ApiService();
export default api;


